import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc, onSnapshot } from '../../firebase.config';
import { useTableSort, SortableHeader } from '../../utils/tableSort';
import { useTablePagination } from '../../utils/tablePagination';
import PaginationControls from '../../utils/PaginationControls';
import { formatCurrency, formatDate, CollapsibleQuickTotals, ReportHeaderWithExport } from './CommonComponents';
import GlobalExportButtons from '../GlobalExportButtons';
import { getCollectionForCompany } from '../../utils/dataAdapter';

const CustomerLedgerReport = ({ db, userId, appId, companyId, dateRange, selectedParty, parties, loading, setLoading, companyDetails }) => {
  const [ledgerData, setLedgerData] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [showQuickSummary, setShowQuickSummary] = useState(false);
  const [quickSummaryEntry, setQuickSummaryEntry] = useState(null);
  const [itemNames, setItemNames] = useState({});
  const [totals, setTotals] = useState({
    totalDebit: 0,
    totalCredit: 0,
    totalOutstanding: 0
  });


  // Table sorting and pagination
  const { sortedData, sortConfig, handleSort } = useTableSort(ledgerData, { key: 'date', direction: 'asc' });
  const pagination = useTablePagination(sortedData, 25);

  // ESC key handler for closing modals
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showQuickSummary) {
          console.log('ESC: Closing Quick Summary modal');
          setShowQuickSummary(false);
          setQuickSummaryEntry(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showQuickSummary]);

  // Function to fetch item names from items collection
  const fetchItemNames = async (itemIds) => {
    if (!itemIds || itemIds.length === 0) return {};
    
    try {
      const itemNames = {};
      
      // Use the same collection path as the rest of the app
      const itemsCollection = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
      if (!itemsCollection) {
        console.error('Items collection not found');
        return {};
      }
      
      // Fetch items one by one to avoid Firestore 'in' query limitations
      for (const itemId of itemIds) {
        try {
          const itemRef = doc(db, itemsCollection.path, itemId);
          const itemSnap = await getDoc(itemRef);
          if (itemSnap.exists()) {
            const data = itemSnap.data();
            itemNames[itemId] = data.name || data.itemName || data.productName || itemId;
            console.log(`Fetched item ${itemId}:`, data.name || data.itemName || data.productName);
          } else {
            itemNames[itemId] = itemId; // Fallback to ID if item not found
            console.log(`Item ${itemId} not found in database`);
          }
        } catch (itemError) {
          console.error(`Error fetching item ${itemId}:`, itemError);
          itemNames[itemId] = itemId; // Fallback to ID if error
        }
      }
      
      return itemNames;
    } catch (error) {
      console.error('Error fetching item names:', error);
      return {};
    }
  };

  // Prepare export data for GlobalExportButtons
  const getExportData = () => {
    if (!selectedParty) return [];
    
    const data = sortedData.map(row => ({
      date: formatDate(row.date),
      type: row.type,
      refNo: row.refNo,
      description: row.description,
      debit: row.debit > 0 ? row.debit : 0,
      credit: row.credit > 0 ? row.credit : 0,
      balance: row.balance
    }));
    
    // Add totals row
    if (ledgerData.length > 0) {
      data.push({
        date: 'TOTALS:',
        type: '',
        refNo: '',
        description: '',
        debit: totals.totalDebit,
        credit: totals.totalCredit,
        balance: totals.totalOutstanding
      });
    }
    
    return data;
  };

  const getExportColumns = () => [
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'refNo', label: 'Ref No' },
    { key: 'description', label: 'Description' },
    { key: 'debit', label: 'Debit' },
    { key: 'credit', label: 'Credit' },
    { key: 'balance', label: 'Balance (Rs)' }
  ];

  const getReportDetails = () => ({
    'Party': selectedParty ? `${parties.find(p => p.id === selectedParty)?.firmName || 'N/A'} (${parties.find(p => p.id === selectedParty)?.partyType || 'N/A'})` : 'N/A',
    'Period': `${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`,
    'Total Debit': totals.totalDebit,
    'Total Credit': totals.totalCredit,
    'Outstanding': totals.totalOutstanding >= 0 
      ? `+${totals.totalOutstanding.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })} (Receivable)` 
      : `-${Math.abs(totals.totalOutstanding).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })} (Payable)`,
    dateRange
  });

  // Handle entry click for quick summary
  const handleEntryClick = async (entry) => {
    setQuickSummaryEntry(entry);
    setShowQuickSummary(true);
    
    // Fetch item names for this entry if it has items
    if (entry.items && entry.items.length > 0) {
      const itemIds = entry.items.map(item => item.item || item.id || item.itemId || item.productId).filter(Boolean);
      console.log('Item IDs to fetch:', itemIds);
      if (itemIds.length > 0) {
        const names = await fetchItemNames(itemIds);
        console.log('Fetched item names:', names);
        setItemNames(prevNames => ({ ...prevNames, ...names }));
      }
    } else {
      // Reset item names if no items
      setItemNames({});
    }
  };



  // Fetch ledger data
  useEffect(() => {
    console.log('CustomerLedgerReport useEffect triggered:', {
      db: !!db,
      userId,
      companyId,
      selectedParty,
      partiesCount: parties.length
    });
    
    if (!db || !userId || !companyId || !selectedParty) {
      setLedgerData([]);
      setTotals({ totalDebit: 0, totalCredit: 0, totalOutstanding: 0 });
      return;
    }

    setLoading(true);
    
    const fetchLedgerData = async () => {
      try {
        // Fetch sales bills using new data adapter
        const salesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
        if (!salesCollectionRef) {
          console.warn('Sales collection not available');
          setLedgerData([]);
          setLoading(false);
          return;
        }
        
        const salesQuery = query(
          salesCollectionRef,
          where('partyId', '==', selectedParty)
        );
        
        // Try alternative party ID field names for sales
        const salesQueryAlt = query(
          salesCollectionRef,
          where('party', '==', selectedParty)
        );
        
        // Fetch purchase bills using new data adapter
        const purchaseCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
        if (!purchaseCollectionRef) {
          console.warn('Purchase collection not available');
        }
        
        const purchaseQuery = purchaseCollectionRef ? query(
          purchaseCollectionRef,
          where('partyId', '==', selectedParty)
        ) : null;
        
        // Try alternative party ID field names for purchases
        const purchaseQueryAlt = purchaseCollectionRef ? query(
          purchaseCollectionRef,
          where('party', '==', selectedParty)
        ) : null;
        
        // Fetch payments using new data adapter
        const paymentsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
        const paymentsQuery = paymentsCollectionRef ? query(
          paymentsCollectionRef,
          where('partyId', '==', selectedParty)
        ) : null;
        
        // Try alternative party ID field names for payments
        const paymentsQueryAlt = paymentsCollectionRef ? query(
          paymentsCollectionRef,
          where('party', '==', selectedParty)
        ) : null;

        // Execute queries with null checks
        const promises = [];
        promises.push(getDocs(salesQuery));
        promises.push(getDocs(salesQueryAlt));
        if (purchaseQuery) promises.push(getDocs(purchaseQuery));
        if (purchaseQueryAlt) promises.push(getDocs(purchaseQueryAlt));
        if (paymentsQuery) promises.push(getDocs(paymentsQuery));
        if (paymentsQueryAlt) promises.push(getDocs(paymentsQueryAlt));
        
        const results = await Promise.all(promises);
        let resultIndex = 0;
        
        const salesSnap = results[resultIndex++];
        const salesSnapAlt = results[resultIndex++];
        const purchaseSnap = purchaseQuery ? results[resultIndex++] : { docs: [] };
        const purchaseSnapAlt = purchaseQueryAlt ? results[resultIndex++] : { docs: [] };
        const paymentsSnap = paymentsQuery ? results[resultIndex++] : { docs: [] };
        const paymentsSnapAlt = paymentsQueryAlt ? results[resultIndex++] : { docs: [] };

        // Combine data from all collection names and field variations
        const allSales = [
          ...salesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          ...salesSnapAlt.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        ];
        
        const allPurchases = [
          ...purchaseSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          ...purchaseSnapAlt.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        ];
        
        const allPayments = [
          ...paymentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          ...paymentsSnapAlt.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        ];
        
        // Filter by date range on client side
        const sales = allSales.filter(sale => {
          const saleDate = sale.invoiceDate || sale.date;
          if (!saleDate) return false;
          const date = new Date(saleDate);
          return date >= dateRange.start && date <= dateRange.end;
        });
        
        const purchases = allPurchases.filter(purchase => {
          const purchaseDate = purchase.billDate || purchase.date;
          if (!purchaseDate) return false;
          const date = new Date(purchaseDate);
          return date >= dateRange.start && date <= dateRange.end;
        });
        
        const payments = allPayments.filter(payment => {
          const paymentDate = payment.paymentDate || payment.date;
          if (!paymentDate) return false;
          const date = new Date(paymentDate);
          return date >= dateRange.start && date <= dateRange.end;
        });
        
        console.log('Raw Data Debug:', {
          allSalesCount: allSales.length,
          allPurchasesCount: allPurchases.length,
          allPaymentsCount: allPayments.length,
          filteredSalesCount: sales.length,
          filteredPurchasesCount: purchases.length,
          filteredPaymentsCount: payments.length,
          sampleSale: sales[0],
          samplePurchase: purchases[0],
          samplePayment: payments[0],
          dateRange: {
            start: dateRange.start.toISOString().split('T')[0],
            end: dateRange.end.toISOString().split('T')[0]
          }
        });

        // Get party opening balance from parties data
        const selectedPartyData = parties.find(p => p.id === selectedParty);
        let partyOpeningBalance = selectedPartyData?.openingBalance || 0;
        
        // Calculate opening balance (transactions before start date) - simplified
        const openingQuery = query(
          salesCollectionRef,
          where('partyId', '==', selectedParty)
        );
        
        const openingPurchaseQuery = purchaseCollectionRef ? query(
          purchaseCollectionRef,
          where('partyId', '==', selectedParty)
        ) : null;
        
        const openingPaymentsQuery = paymentsCollectionRef ? query(
          paymentsCollectionRef,
          where('partyId', '==', selectedParty)
        ) : null;

        const openingPromises = [getDocs(openingQuery)];
        if (openingPurchaseQuery) openingPromises.push(getDocs(openingPurchaseQuery));
        if (openingPaymentsQuery) openingPromises.push(getDocs(openingPaymentsQuery));
        
        const openingResults = await Promise.all(openingPromises);
        let openingIndex = 0;
        
        const openingSalesSnap = openingResults[openingIndex++];
        const openingPurchaseSnap = openingPurchaseQuery ? openingResults[openingIndex++] : { docs: [] };
        const openingPaymentsSnap = openingPaymentsQuery ? openingResults[openingIndex++] : { docs: [] };

        const allOpeningSales = openingSalesSnap.docs.map(doc => doc.data());
        const allOpeningPurchases = openingPurchaseSnap.docs.map(doc => doc.data());
        const allOpeningPayments = openingPaymentsSnap.docs.map(doc => doc.data());

        // Filter opening transactions by date on client side
        const openingSales = allOpeningSales.filter(sale => {
          const saleDate = sale.invoiceDate || sale.date;
          if (!saleDate) return false;
          const date = new Date(saleDate);
          return date < dateRange.start;
        });
        
        const openingPurchases = allOpeningPurchases.filter(purchase => {
          const purchaseDate = purchase.billDate || purchase.date;
          if (!purchaseDate) return false;
          const date = new Date(purchaseDate);
          return date < dateRange.start;
        });
        
        const openingPayments = allOpeningPayments.filter(payment => {
          const paymentDate = payment.paymentDate || payment.date;
          if (!paymentDate) return false;
          const date = new Date(paymentDate);
          return date < dateRange.start;
        });

        let openingBalance = partyOpeningBalance; // Start with party's opening balance
        
        // Add sales (receivable)
        openingSales.forEach(sale => {
          const totalAmount = parseFloat(sale.totalAmount || sale.grandTotal || 0);
          openingBalance += totalAmount;
        });
        
        // Subtract purchases (payable)
        openingPurchases.forEach(purchase => {
          const totalAmount = parseFloat(purchase.totalAmount || purchase.grandTotal || 0);
          openingBalance -= totalAmount;
        });
        
        // Add payments received (ignore ADV-INV advance allocations)
        openingPayments.forEach(payment => {
          const ref = (payment.receiptNumber || payment.paymentId || payment.number || '').toString().toUpperCase();
          if (ref.startsWith('ADV-INV')) return; // ignore advance allocation adjustments
          const amount = parseFloat(payment.amount || 0);
          openingBalance -= amount; // Payment reduces receivable
        });

        setOpeningBalance(openingBalance);

        // Process current period transactions (collect first; compute balances after sorting)
        const ledgerEntries = [];

        // Add sales entries (Debit)
        sales.forEach(sale => {
          const totalAmount = parseFloat(sale.totalAmount || sale.grandTotal || sale.amount || sale.invoiceAmount || 0);
          if (totalAmount > 0) {
            ledgerEntries.push({
              transactionId: sale.id,
              date: sale.invoiceDate || sale.date,
              type: 'Sale',
              refNo: sale.invoiceNo || sale.billNo || sale.invoiceNumber || sale.number || sale.id,
              description: `Sale Invoice - ${sale.invoiceNo || sale.billNo || sale.invoiceNumber || sale.number || sale.id}`,
              debit: totalAmount,
              credit: 0,
              balance: 0,
              items: sale.rows || sale.items || []
            });
          }
        });

        // Add purchase entries
        purchases.forEach(purchase => {
          const totalAmount = parseFloat(purchase.totalAmount || purchase.grandTotal || purchase.amount || purchase.billAmount || 0);
          if (totalAmount > 0) {
            ledgerEntries.push({
              transactionId: purchase.id,
              date: purchase.billDate || purchase.date,
              type: 'Purchase',
              refNo: purchase.billNo || purchase.invoiceNo || purchase.billNumber || purchase.number || purchase.id,
              description: `Purchase Bill - ${purchase.billNo || purchase.invoiceNo || purchase.billNumber || purchase.number || purchase.id}`,
              debit: 0,
              credit: totalAmount,
              balance: 0,
              items: purchase.rows || purchase.items || []
            });
          }
        });

        // Add payments: classify by receipt number prefix
        payments.forEach(payment => {
          const amount = parseFloat(payment.amount || payment.totalAmount || payment.paymentAmount || 0);
          const ref = (payment.receiptNumber || payment.paymentId || payment.number || '').toString().toUpperCase();
          if (amount <= 0) return;

          // Skip advance allocation to invoice (re-entry): ADV-INV*
          if (ref.startsWith('ADV-INV')) {
            return; // ignore from ledger and totals
          }

          // Check for PRP first (more specific), then PRI
          // Handle cases where receiptNumber might be "PRI PRP25-26/18" - extract actual reference
          const isPRP = ref.includes('PRP') && (ref.startsWith('PRP') || ref.includes(' PRP')); // Payment paid against purchase → Debit
          const isPRI = !isPRP && ref.startsWith('PRI'); // Payment received against sales → Credit

          if (isPRI) {
            ledgerEntries.push({
              transactionId: payment.id,
              date: payment.paymentDate || payment.date,
              type: 'Payment (PRI)',
              refNo: payment.receiptNumber || payment.paymentId || payment.number || payment.id,
              description: `Payment Received - ${payment.paymentMode || 'Cash'}${payment.reference ? ` (${payment.reference})` : ''}`,
              debit: 0,
              credit: amount,
              balance: 0,
              items: []
            });
            return;
          }

          if (isPRP) {
            ledgerEntries.push({
              transactionId: payment.id,
              date: payment.paymentDate || payment.date,
              type: 'Payment (PRP)',
              refNo: payment.receiptNumber || payment.paymentId || payment.number || payment.id,
              description: `Payment Against Purchase - ${payment.paymentMode || 'Cash'}${payment.reference ? ` (${payment.reference})` : ''}`,
              debit: amount,
              credit: 0,
              balance: 0,
              items: []
            });
            return;
          }

          // Fallback: if no prefix, treat as received (credit)
          ledgerEntries.push({
            transactionId: payment.id,
            date: payment.paymentDate || payment.date,
            type: 'Payment',
            refNo: payment.receiptNumber || payment.paymentId || payment.number || payment.id,
            description: `Payment - ${payment.paymentMode || 'Cash'}${payment.reference ? ` (${payment.reference})` : ''}`,
            debit: 0,
            credit: amount,
            balance: 0,
            items: []
          });
        });

        // Sort chronologically: by date, then by trailing numeric in refNo
        const getSeq = (ref) => {
          const m = (ref || '').toString().match(/(\d+)(?!.*\d)/);
          return m ? parseInt(m[1], 10) : 0;
        };
        const typeWeight = (t) => {
          // Keep it simple but stable if date+seq tie
          if (t?.startsWith('Sale')) return 1;
          if (t === 'Purchase') return 2;
          if (t === 'Payment (PRP)') return 3;
          if (t === 'Payment (PRI)') return 4;
          if (t === 'Payment') return 5;
          return 9;
        };
        ledgerEntries.sort((a, b) => {
          const da = new Date(a.date).getTime();
          const db = new Date(b.date).getTime();
          if (da !== db) return da - db;
          const sa = getSeq(a.refNo);
          const sb = getSeq(b.refNo);
          if (sa !== sb) return sa - sb;
          return typeWeight(a.type) - typeWeight(b.type);
        });

        // Recompute running balance and totals strictly in sorted order
        let currentBalance = openingBalance;
        let totalDebit = 0;
        let totalCredit = 0;
        for (let i = 0; i < ledgerEntries.length; i += 1) {
          const e = ledgerEntries[i];
          if (e.debit) {
            currentBalance += e.debit;
            totalDebit += e.debit;
          }
          if (e.credit) {
            currentBalance -= e.credit;
            totalCredit += e.credit;
          }
          e.balance = currentBalance;
        }

        console.log('Customer Ledger Debug:', {
          sales: sales.length,
          purchases: purchases.length,
          payments: payments.length,
          ledgerEntries: ledgerEntries.length,
          openingBalance,
          currentBalance,
          totalDebit,
          totalCredit
        });
        
        setLedgerData(ledgerEntries);
        setClosingBalance(currentBalance);
        setTotals({
          totalDebit,
          totalCredit,
          totalOutstanding: currentBalance
        });

      } catch (error) {
        console.error('Error fetching ledger data:', error);
        setLedgerData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLedgerData();
  }, [db, userId, companyId, selectedParty, dateRange, setLoading]);

  // Check if party is selected
  if (!selectedParty) {
    return (
      <div className="p-3 sm:p-6">
        <div className="text-center text-gray-500">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">No Party Selected</h3>
            <p>Please select a party from the "Party (Optional)" filter above to view their ledger.</p>
          </div>
          {parties.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-gray-400 mb-2">Available parties:</p>
              <div className="text-sm text-gray-500">
                {parties.slice(0, 5).map(party => (
                  <div key={party.id} className="mb-1">
                    • {party.firmName} ({party.partyType || 'Unknown Type'})
                  </div>
                ))}
                {parties.length > 5 && <div>... and {parties.length - 5} more</div>}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const selectedPartyObj = parties.find(p => p.id === selectedParty);
  
  if (!selectedPartyObj) {
    return (
      <div className="p-3 sm:p-6">
        <div className="text-center text-gray-500">
          Selected party not found
        </div>
      </div>
    );
  }

  // Letterhead component
  const Letterhead = () => (
    <div className="mb-6 border-b-2 border-gray-300 pb-4">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          {companyDetails?.logoUrl && (
            <img 
              src={companyDetails.logoUrl} 
              alt="Company Logo" 
              className="h-16 mb-2"
            />
          )}
          <h1 className="text-2xl font-bold text-gray-800 mb-1">
            {companyDetails?.firmName || 'Company Name'}
          </h1>
          <div className="text-sm text-gray-600 space-y-1">
            {companyDetails?.address && <p>{companyDetails.address}</p>}
            {companyDetails?.city && companyDetails?.state && (
              <p>{companyDetails.city}, {companyDetails.state} {companyDetails?.pincode}</p>
            )}
            {companyDetails?.gstin && <p>GSTIN: {companyDetails.gstin}</p>}
            {companyDetails?.contactNumber && <p>Phone: {companyDetails.contactNumber}</p>}
            {companyDetails?.email && <p>Email: {companyDetails.email}</p>}
          </div>
        </div>
        <div className="text-right text-sm text-gray-600">
          <p>Report Type: Party Ledger</p>
          <p>Generated: {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );

  // Main return statement
  return (
    <div className="p-3 sm:p-6">
      <ReportHeaderWithExport
        title={`Party Ledger (${selectedPartyObj?.firmName} - ${selectedPartyObj?.partyType})`}
        subtitle={`Period: ${formatDate(dateRange.start)} to ${formatDate(dateRange.end)}`}
        exportButtons={
          <GlobalExportButtons
            data={getExportData()}
            columns={getExportColumns()}
            filename="CUSTOMER_LEDGER"
            title="Customer Ledger Report"
            companyDetails={companyDetails}
            reportDetails={getReportDetails()}
            disabled={ledgerData.length === 0}
          />
        }
      />

      <CollapsibleQuickTotals
        cards={[
          { label: 'Opening Balance', value: openingBalance >= 0 ? `${formatCurrency(openingBalance)} Cr` : `${formatCurrency(Math.abs(openingBalance))} Dr`, color: 'blue' },
          { label: 'Total Debit', value: formatCurrency(totals.totalDebit), color: 'green' },
          { label: 'Total Credit', value: formatCurrency(totals.totalCredit), color: 'red' },
          { label: 'Closing Balance', value: closingBalance >= 0 ? `${formatCurrency(closingBalance)} Cr` : `${formatCurrency(Math.abs(closingBalance))} Dr`, color: 'purple' }
        ]}
      />

      {/* Mobile ledger cards */}
      <div className="md:hidden space-y-2 mb-4">
        {pagination.currentData.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm bg-white rounded-lg border">No ledger entries found</div>
        ) : pagination.currentData.map((row, idx) => (
          <button
            key={row.transactionId || idx}
            type="button"
            onClick={() => handleEntryClick(row)}
            className="w-full text-left bg-white border border-gray-200 rounded-lg p-3 shadow-sm touch-manipulation active:bg-blue-50"
          >
            <div className="flex justify-between items-start gap-2 mb-1">
              <span className="text-xs text-gray-500">{formatDate(row.date)}</span>
              <span className="text-xs font-medium text-blue-600">{row.type}</span>
            </div>
            <div className="font-medium text-gray-900 text-sm mb-1">{row.refNo}</div>
            <div className="text-xs text-gray-600 line-clamp-2 mb-2">{row.description}</div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{row.debit > 0 ? `Dr ${formatCurrency(row.debit)}` : row.credit > 0 ? `Cr ${formatCurrency(row.credit)}` : '—'}</span>
              <span className={`font-semibold ${row.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {row.balance >= 0 ? `${formatCurrency(row.balance)} Cr` : `${formatCurrency(Math.abs(row.balance))} Dr`}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Ledger Table */}
      <div className="hidden md:block bg-white border border-gray-200 rounded-lg report-scroll-x">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortableHeader columnKey="date" label="Date" onSort={handleSort} sortConfig={sortConfig} />
              <SortableHeader columnKey="type" label="Type" onSort={handleSort} sortConfig={sortConfig} />
              <SortableHeader columnKey="refNo" label="Ref No" onSort={handleSort} sortConfig={sortConfig} />
              <SortableHeader columnKey="description" label="Description" onSort={handleSort} sortConfig={sortConfig} />
              <SortableHeader columnKey="debit" label="Debit" onSort={handleSort} sortConfig={sortConfig} />
              <SortableHeader columnKey="credit" label="Credit" onSort={handleSort} sortConfig={sortConfig} />
              <SortableHeader columnKey="balance" label="Balance" onSort={handleSort} sortConfig={sortConfig} />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pagination.currentData.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-gray-400">No ledger entries found</td></tr>
            ) : pagination.currentData.map((row, idx) => (
              <tr key={row.transactionId || idx} className="cursor-pointer hover:bg-blue-50" onClick={() => handleEntryClick(row)}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatDate(row.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {row.type}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                  {row.refNo}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {row.description}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {row.debit > 0 ? formatCurrency(row.debit) : ''}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {row.credit > 0 ? formatCurrency(row.credit) : ''}
                </td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${row.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {row.balance >= 0 ? `${formatCurrency(row.balance)} Cr` : `${formatCurrency(Math.abs(row.balance))} Dr`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <PaginationControls {...pagination} />
      
      {/* Quick Summary Modal */}
      {showQuickSummary && quickSummaryEntry && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-2xl relative max-h-[92dvh] overflow-y-auto">
            <button className="absolute top-2 right-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-800 text-xl touch-manipulation" onClick={() => setShowQuickSummary(false)} aria-label="Close">&times;</button>
            <h3 className="text-lg font-bold mb-4 text-center">Transaction Summary</h3>
            <div className="space-y-3">
              <div><strong>Date:</strong> {formatDate(quickSummaryEntry.date)}</div>
              <div><strong>Type:</strong> {quickSummaryEntry.type}</div>
              <div><strong>Reference:</strong> {quickSummaryEntry.refNo}</div>
              <div><strong>Description:</strong> {quickSummaryEntry.description}</div>
              <div><strong>Debit:</strong> {quickSummaryEntry.debit > 0 ? formatCurrency(quickSummaryEntry.debit) : '-'}</div>
              <div><strong>Credit:</strong> {quickSummaryEntry.credit > 0 ? formatCurrency(quickSummaryEntry.credit) : '-'}</div>
              <div><strong>Balance:</strong> 
                <span className={`ml-2 ${quickSummaryEntry.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {quickSummaryEntry.balance >= 0 ? `${formatCurrency(quickSummaryEntry.balance)} Cr` : `${formatCurrency(Math.abs(quickSummaryEntry.balance))} Dr`}
                </span>
              </div>

              {/* Items List */}
              {quickSummaryEntry.items && quickSummaryEntry.items.length > 0 && (
                <div>
                  <strong>Items:</strong>
                  <div className="mt-2 space-y-1">
                    {quickSummaryEntry.items.map((item, index) => {
                      const itemId = item.item || item.id || item.itemId || item.productId;
                      console.log(`CustomerLedger Item ${index}:`, item);
                      console.log(`CustomerLedger Item ID for ${index}:`, itemId);
                      console.log(`CustomerLedger Current itemNames state:`, itemNames);
                      const itemName = itemNames[itemId] || item.itemName || item.name || item.productName || item.product || itemId || 'Unknown Item';
                      console.log(`CustomerLedger Final item name for ${index}:`, itemName);
                      return (
                        <div key={index} className="text-sm bg-gray-50 p-2 rounded">
                          <div><strong>Item:</strong> {itemName}</div>
                          <div><strong>Qty:</strong> {item.qty || item.nos || 0}</div>
                          <div><strong>Rate:</strong> {formatCurrency(item.rate || 0)}</div>
                          <div><strong>Amount:</strong> {formatCurrency(item.amount || item.total || 0)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerLedgerReport; 